import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trackClick } from '../services/analyticsService';
import { loadLocale } from '../i18n';

// Grouped by region — a thin divider renders between groups in the menu so
// the four options read as two families rather than one flat list.
const LANG_GROUPS = [
  [
    { code: 'es', label: 'ES', flag: '🇲🇽' },
    { code: 'en', label: 'EN', flag: '🇺🇸' },
  ],
  [
    { code: 'zh', label: '中文', flag: '🇨🇳' },
    { code: 'ja', label: '日本語', flag: '🇯🇵' },
  ],
];
const LANGS = LANG_GROUPS.flat();

/**
 * @param {'down'|'up'} menuPlacement  Which way the dropdown opens. Use 'up'
 *   when the trigger sits near the bottom of its container (e.g. the mobile
 *   sidebar) so the menu doesn't overflow past the viewport edge.
 */
const LanguageSwitcher = ({ menuPlacement = 'down' }) => {
  const { t } = useTranslation('common');
  const { i18n } = useTranslation();
  const current = (i18n.resolvedLanguage || i18n.language || 'es').slice(0, 2);
  const currentLang = LANGS.find((lang) => lang.code === current) || LANGS[0];

  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);

  const menuItems = () => containerRef.current?.querySelectorAll('[role="menuitemradio"]') || [];

  useEffect(() => {
    if (!open) return undefined;
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  // role="menu" implies arrow-key navigation with focus moving into the
  // menu on open — land on the active language so arrow keys work right
  // away, same as a native <select> would.
  useEffect(() => {
    if (!open) return;
    const items = menuItems();
    const activeIndex = Math.max(0, LANGS.findIndex((lang) => lang.code === current));
    items[activeIndex]?.focus();
  }, [open, current]);

  const handleMenuKeyDown = (e) => {
    const items = menuItems();
    const count = items.length;
    if (count === 0) return;
    const activeIndex = Array.prototype.indexOf.call(items, document.activeElement);
    const focusAt = (index) => items[index]?.focus();

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focusAt(activeIndex < 0 ? 0 : (activeIndex + 1) % count);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusAt(activeIndex < 0 ? count - 1 : (activeIndex - 1 + count) % count);
        break;
      case 'Home':
        e.preventDefault();
        focusAt(0);
        break;
      case 'End':
        e.preventDefault();
        focusAt(count - 1);
        break;
      case 'Tab':
        // Let focus leave the widget normally, just close the now-orphaned menu.
        setOpen(false);
        break;
      default:
        break;
    }
  };

  const handleChange = async (code) => {
    setOpen(false);
    if (code === current) return;
    // Fetch zh/ja's namespace JSON before switching — avoids even the brief
    // Spanish-fallback flash for the common case (a manual click), though
    // changeLanguage() would recover gracefully either way. No-op for es/en.
    await loadLocale(code);
    i18n.changeLanguage(code);
    // Spanish label for analytics consistency — every event the platform fires
    // is Spanish-labelled regardless of the user's locale, so the analytics
    // dashboard reads cleanly without cross-locale grouping logic.
    trackClick('language-switcher', 'button', `Idioma: ${code.toUpperCase()}`, {
      from: current,
      to: code,
    });
  };

  return (
    <div className="language-switcher" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        className="lang-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('header.language')}
      >
        <span className="lang-flag" aria-hidden="true">{currentLang.flag}</span>
        <span className="lang-code">{currentLang.label}</span>
        <span className={`lang-chevron${open ? ' lang-chevron-open' : ''}`} aria-hidden="true">▾</span>
      </button>

      {open && (
        <div
          className={`lang-menu${menuPlacement === 'up' ? ' lang-menu-up' : ''}`}
          role="menu"
          onKeyDown={handleMenuKeyDown}
        >
          {LANG_GROUPS.map((group, groupIdx) => (
            <div className="lang-group" key={groupIdx}>
              {groupIdx > 0 && <div className="lang-group-divider" aria-hidden="true" />}
              {group.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  role="menuitemradio"
                  aria-checked={current === lang.code}
                  tabIndex={-1}
                  className={`lang-option${current === lang.code ? ' lang-option-active' : ''}`}
                  onClick={() => handleChange(lang.code)}
                >
                  <span className="lang-flag" aria-hidden="true">{lang.flag}</span>
                  <span className="lang-name">{lang.label}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
